import { createSlice } from "@reduxjs/toolkit";

export const initialState = {
  user: {},
  role: null,
  permissions: {},
  error: "", // for error message
  authErrorField: null as null | "email" | "otp" | "global",
  loading: false,
  isUserLogout: false,
  errorMsg: false, // for error
  otpStep: "email" as "email" | "otp",
  otpEmail: "",
  otpPolicy: null as null | {
    expiresInSeconds?: number;
    resendAvailableInSeconds?: number;
    maxAttempts?: number;
    maxResends?: number;
  },
  resendsRemaining: null as number | null,
  resendSecondsRemaining: 0,
  otpAttemptsBlocked: false,
};

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    apiError(state, action) {
      state.error = action.payload.data;
      state.authErrorField = action.payload.field || "global";
      state.loading = false; // Changed from true to false - loading should be false when there's an error
      state.isUserLogout = false;
      state.errorMsg = true;
      if (typeof action.payload.otpAttemptsBlocked === "boolean") {
        state.otpAttemptsBlocked = action.payload.otpAttemptsBlocked;
      }
    },
    loginSuccess(state, action) {
      const payload = action.payload;
      
      // Handle admin users (subRole: null) explicitly
      const userRole = payload.role !== undefined ? payload.role : 
                      payload.subRole !== undefined ? payload.subRole : null;
      
      state.user = payload.user || payload;
      state.role = userRole;
      state.permissions = payload.permissions || {};
      state.loading = false;
      state.errorMsg = false;
      state.authErrorField = null;
      
      // Ensure admin users have subRole: null in the user object
      if (state.user && userRole === null) {
        (state.user as any).subRole = null;
        (state.user as any).role = null;
        console.log('LoginSuccess: Admin user detected, set subRole and role to null in user object');
      }
      
      console.log('LoginSuccess: Final state:', {
        user: state.user,
        role: state.role,
        userSubRole: (state.user as any).subRole,
        userRole: (state.user as any).role
      });
    },
    otpRequestSuccess(state, action) {
      const payload = action.payload || {};
      const data = payload.data || {};
      state.otpStep = "otp";
      state.otpEmail = payload.email || state.otpEmail;
      state.otpPolicy = {
        expiresInSeconds: data.expiresInSeconds,
        resendAvailableInSeconds: data.resendAvailableInSeconds,
        maxAttempts: data.maxAttempts,
        maxResends: data.maxResends,
      };
      state.resendsRemaining = typeof data.resendsRemaining === "number" ? data.resendsRemaining : state.resendsRemaining;
      state.resendSecondsRemaining = Number(data.resendAvailableInSeconds || 0);
      state.otpAttemptsBlocked = false;
      state.loading = false;
      state.error = "";
      state.authErrorField = null;
      state.errorMsg = false;
    },
    setOtpCooldown(state, action) {
      state.otpStep = "otp";
      if (action.payload?.email) state.otpEmail = action.payload.email;
      state.resendSecondsRemaining = Number(action.payload?.seconds || 0);
      state.error = "";
      state.authErrorField = null;
      state.errorMsg = false;
      state.loading = false;
      state.otpAttemptsBlocked = false;
    },
    setResendsRemaining(state, action) {
      state.resendsRemaining = action.payload;
      state.loading = false;
    },
    decrementOtpCooldown(state) {
      state.resendSecondsRemaining = Math.max(0, Number(state.resendSecondsRemaining || 0) - 1);
    },
    resetOtpLogin(state) {
      state.otpStep = "email";
      state.otpEmail = "";
      state.otpPolicy = null;
      state.resendsRemaining = null;
      state.resendSecondsRemaining = 0;
      state.otpAttemptsBlocked = false;
      state.error = "";
      state.authErrorField = null;
      state.errorMsg = false;
      state.loading = false;
    },
    logoutUserSuccess(state, action) {
      state.isUserLogout = true;
      state.user = {};
      state.role = null;
      state.permissions = {};
    },
    reset_login_flag(state) {
      state.error = ""
      state.authErrorField = null;
      state.loading = false;
      state.errorMsg = false;
    },
    setPermissions(state, action) {
      state.permissions = action.payload || {};
    }
  },
});

export const {
  setLoading,
  apiError,
  loginSuccess,
  logoutUserSuccess,
  reset_login_flag,
  setPermissions,
  otpRequestSuccess,
  setOtpCooldown,
  setResendsRemaining,
  decrementOtpCooldown,
  resetOtpLogin,
} = loginSlice.actions

export default loginSlice.reducer;
